import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Search, BookOpen, Video } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.azalega-academy.com';
const API_URL = API_BASE.endsWith('/api/v1') ? API_BASE : `${API_BASE}/api/v1`;

const IndicatorCoursesPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Predefined indicators with icons (fallback if no courses found)
    const indicatorDefaults = {
        'sft': { icon: '📊', iconBg: 'bg-green-500', name: 'SFT – Smart Flip Trader' },
        'oms': { icon: '🎯', iconBg: 'bg-orange-500', name: 'OMS – One Minute Sniper' },
        'ste': { icon: '⚡', iconBg: 'bg-purple-500', name: 'STE – Smart Trend Engine' }
    };

    useEffect(() => {
        fetchIndicatorCourses();
    }, []);

    const fetchIndicatorCourses = async () => {
        try {
            // Fetch all published courses (we'll filter client-side to catch both 'sistem' and 'indicator' categories)
            const response = await axios.get(`${API_URL}/courses`, {
                params: {
                    limit: 100,
                    published: true
                }
            });

            const fetchedCourses = response.data.courses || response.data || [];

            // Filter for relevant courses (category 'sistem' OR 'indicator')
            const relevantCourses = fetchedCourses.filter(course =>
                course.category === 'sistem' ||
                course.category === 'indicator' ||
                (course.videoSubcategory === 'indicator')
            );

            // Group courses by indicatorName
            const groups = {};

            relevantCourses.forEach(course => {
                // Key is indicatorName (normalized) or unique ID if no name
                const key = course.indicatorName
                    ? course.indicatorName.trim().toUpperCase()
                    : course._id;

                if (!groups[key]) {
                    groups[key] = {
                        id: course._id, // Default to first found
                        mainCourse: course, // Store reference for defaults
                        courses: []
                    };
                }

                groups[key].courses.push(course);

                // If this is a video course, prioritize it as the "Main" display course
                if (course.courseType === 'video') {
                    groups[key].id = course._id;
                    groups[key].mainCourse = course;
                }
                // If mainCourse doesn't have thumbnail but this one does, use this one's thumbnail
                else if (!groups[key].mainCourse.thumbnail && course.thumbnail) {
                    groups[key].mainCourse.thumbnail = course.thumbnail;
                }
            });

            // Map groups to indicator format
            const mappedCourses = Object.values(groups).map(group => {
                const course = group.mainCourse;

                // Calculate total lessons/duration across all courses in group
                const totalLessons = group.courses.reduce((acc, c) =>
                    acc + (c.totalLessons || c.modules?.reduce((mAcc, m) => mAcc + (m.lessons?.length || 0), 0) || 0), 0);

                const totalDuration = group.courses.reduce((acc, c) =>
                    acc + (c.totalDuration || 0), 0);

                // Try to match course tags/title to indicator type (using main course)
                const indicator = Object.keys(indicatorDefaults).find(key =>
                    course.tags?.includes(key.toLowerCase()) ||
                    course.title?.toLowerCase().includes(key.toLowerCase())
                );

                const defaults = indicator ? indicatorDefaults[indicator] : {
                    icon: '📈',
                    iconBg: 'bg-blue-500',
                    name: course.title
                };

                return {
                    id: group.id, // Links to the preferred (Video) course
                    name: course.title,
                    description: course.shortDescription || course.description,
                    icon: defaults.icon,
                    iconBg: defaults.iconBg,
                    lessons: totalLessons,
                    duration: totalDuration ? `${Math.floor(totalDuration / 60)} min` : 'N/A',
                    route: `/sistem/indicator/${group.id}`,
                    label: course.indicatorName || (indicator ? indicator.toUpperCase() : 'Indicator'),
                    thumbnail: course.thumbnail
                };
            });

            setCourses(mappedCourses);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching indicator courses:', error);
            setLoading(false);
        }
    };

    const filteredIndicators = courses.filter(indicator =>
        indicator.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        indicator.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black">
            {/* Header Section - WHITE BACKGROUND */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-gray-800 py-8 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <Link to="/student/dashboard" className="hover:text-gray-900 dark:hover:text-white transition-colors">Dashboard</Link>
                        <span className="mx-2">›</span>
                        <Link to="/my-courses" className="hover:text-gray-900 dark:hover:text-white transition-colors">Courses</Link>
                        <span className="mx-2">›</span>
                        <span className="text-gray-900 dark:text-white font-medium">Indicator Courses</span>
                    </div>

                    <div className="flex items-center mb-6">
                        <div className="bg-green-500 p-3 rounded-xl mr-4">
                            <TrendingUp size={28} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Indicator Courses</h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Master technical indicators, market analysis, and trading signals with expert guidance and practical examples.
                            </p>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:!bg-black rounded-lg p-4 border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center">
                                <div className="bg-green-500 p-2 rounded-lg mr-3">
                                    <BookOpen size={18} className="text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{courses.length}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Indicators</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center">
                                <div className="bg-blue-500 p-2 rounded-lg mr-3">
                                    <Video size={18} className="text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {courses.reduce((acc, c) => acc + c.lessons, 0)}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Lessons</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center">
                                <div className="bg-purple-500 p-2 rounded-lg mr-3">
                                    <span className="text-white text-lg">⏱️</span>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {courses.reduce((acc, c) => {
                                            const duration = parseInt(c.duration) || 0;
                                            return acc + duration;
                                        }, 0)}min
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Content</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center">
                                <div className="bg-yellow-500 p-2 rounded-lg mr-3">
                                    <span className="text-white text-lg">⭐</span>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">4.8</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Avg Rating</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search & Filter Section */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-gray-800 py-6 px-6 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search indicator courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-black placeholder-gray-500"
                            />
                        </div>
                        <div className="md:w-64">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-black"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="name">Name (A-Z)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Courses Grid */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="text-gray-500 dark:text-gray-400 mt-4">Loading courses...</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredIndicators.map((indicator) => (
                                <Link
                                    key={indicator.id}
                                    to={indicator.route}
                                    className="group bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300"
                                >
                                    {/* Card Header with Icon/Thumbnail */}
                                    <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                                        <div className="flex items-center justify-between mb-4">
                                            {indicator.thumbnail ? (
                                                <div className="w-16 h-16 rounded-xl shadow-lg overflow-hidden group-hover:scale-110 transition-transform bg-[#ffffff] dark:bg-black">
                                                    <img
                                                        src={indicator.thumbnail.startsWith('http') ? indicator.thumbnail : `${API_URL.replace('/api/v1', '')}${indicator.thumbnail}`}
                                                        alt={indicator.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ) : (
                                                <div className={`${indicator.iconBg} w-16 h-16 rounded-xl flex items-center justify-center text-3xl shadow-lg group-hover:scale-110 transition-transform`}>
                                                    {indicator.icon}
                                                </div>
                                            )}
                                            <div className="text-right">
                                                <div className="text-sm text-gray-500 dark:text-gray-400">{indicator.label}</div>
                                            </div>
                                        </div>

                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 transition-colors">
                                            {indicator.name}
                                        </h3>

                                        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                                            {indicator.description}
                                        </p>
                                    </div>

                                    {/* Card Footer */}
                                    <div className="p-6 bg-gray-50 dark:bg-black group-hover:bg-blue-50 dark:group-hover:bg-black transition-colors">
                                        <div className="flex items-center justify-between text-sm mb-4">
                                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                <BookOpen size={16} className="mr-1" />
                                                <span>{indicator.lessons} lessons</span>
                                            </div>
                                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                <span className="mr-1">⏱️</span>
                                                <span>{indicator.duration}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-500">Progress: 0%</span>
                                            <span className="text-blue-600 dark:text-blue-400 font-semibold group-hover:text-blue-700 dark:group-hover:text-blue-300 text-sm">
                                                Start Learning →
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* No Results */}
                        {!loading && filteredIndicators.length === 0 && (
                            <div className="text-center py-12">
                                <div className="text-gray-400 mb-2">
                                    <Search size={48} className="mx-auto" />
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 text-lg">
                                    {searchQuery
                                        ? `No indicators found matching "${searchQuery}"`
                                        : 'No indicator courses available yet. Instructor needs to create courses with category "indicator".'}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default IndicatorCoursesPage;
